import React, { useEffect, useMemo, useState } from 'react';
import { CalendarPlus, Plus, Search, Trash2, Undo2, Pencil, Users, History } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { adminAPI, eventsAPI } from '../services/api';

const todayISO = () => {
  const now = new Date();
  const tzOffset = now.getTimezoneOffset() * 60000;
  return new Date(now - tzOffset).toISOString().slice(0, 10);
};

// Mês atual usa o dia de hoje; outro mês usa o dia 1 (o ranking agrega por mês).
const monthToAwardDate = (month) => {
  const today = todayISO();
  return month === today.slice(0, 7) ? today : `${month}-01`;
};

// ---------------------------------------------------------------------------
// Diálogo de criação/edição de evento
// ---------------------------------------------------------------------------
const EventFormDialog = ({ open, onOpenChange, event, onSaved }) => {
  const isEdit = Boolean(event);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultPoints, setDefaultPoints] = useState('100');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(event?.name || '');
      setDescription(event?.description || '');
      setDefaultPoints(String(event?.default_points ?? 100));
    }
  }, [open, event]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      toast.error('Dê um nome ao evento (mínimo 2 caracteres).');
      return;
    }
    const points = Number(defaultPoints);
    if (!Number.isFinite(points) || points < 0) {
      toast.error('Pontuação padrão inválida.');
      return;
    }
    setSaving(true);
    try {
      const payload = { name: trimmed, description: description.trim() || null, default_points: Math.round(points) };
      if (isEdit) {
        await eventsAPI.updateEvent(event.id, payload);
        toast.success('Evento atualizado.');
      } else {
        await eventsAPI.createEvent(payload);
        toast.success('Evento criado.');
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível salvar o evento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-950 border-gray-800 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">{isEdit ? 'Editar evento' : 'Novo evento'}</DialogTitle>
          <DialogDescription className="text-gray-400">
            Eventos com pontuação padrão sugerida no lançamento.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-300">Nome</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Imersão Elite Q3"
              className="mt-1 bg-gray-900 border-gray-800 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-300">Descrição (opcional)</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Contexto do evento"
              className="mt-1 bg-gray-900 border-gray-800 text-white"
              rows={2}
            />
          </div>
          <div>
            <label className="text-sm text-gray-300">Pontuação padrão</label>
            <Input
              type="number"
              min={0}
              value={defaultPoints}
              onChange={(e) => setDefaultPoints(e.target.value)}
              className="mt-1 w-40 bg-gray-900 border-gray-800 text-white"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-gray-700 text-gray-200" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Aba 1 — Atribuir pontos
// ---------------------------------------------------------------------------
const AssignTab = ({ events, members, loading, reloadEvents, onAwarded }) => {
  const [eventId, setEventId] = useState('');
  const [awardMonth, setAwardMonth] = useState(todayISO().slice(0, 7));
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({}); // { user_id: pointsString }
  const [submitting, setSubmitting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const selectedEvent = useMemo(() => events.find((e) => e.id === eventId) || null, [events, eventId]);

  useEffect(() => {
    // Ao trocar de evento, atualiza a pontuação dos membros ainda não editados manualmente.
    if (!selectedEvent) return;
    setSelected((prev) => {
      const next = {};
      Object.entries(prev).forEach(([uid, value]) => {
        next[uid] = value?.touched
          ? value
          : { points: String(selectedEvent.default_points), touched: false };
      });
      return next;
    });
  }, [selectedEvent]);

  const filteredMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return members.filter((m) => !term || m.full_name.toLowerCase().includes(term));
  }, [members, search]);

  const selectedIds = Object.keys(selected);

  const toggleMember = (member, checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (checked) {
        next[member.id] = {
          points: String(selectedEvent?.default_points ?? 100),
          touched: false,
        };
      } else {
        delete next[member.id];
      }
      return next;
    });
  };

  const setMemberPoints = (userId, value) => {
    setSelected((prev) => ({ ...prev, [userId]: { points: value, touched: true } }));
  };

  const handleSubmit = async () => {
    if (!eventId) {
      toast.error('Selecione um evento.');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos um membro.');
      return;
    }
    const entries = [];
    for (const uid of selectedIds) {
      const points = Number(selected[uid].points);
      if (!Number.isFinite(points) || points < 0) {
        const name = members.find((m) => m.id === uid)?.full_name || uid;
        toast.error(`Pontuação inválida para ${name}.`);
        return;
      }
      entries.push({ user_id: uid, points: Math.round(points) });
    }
    setSubmitting(true);
    try {
      const { data } = await eventsAPI.awardPoints(eventId, {
        entries,
        award_date: monthToAwardDate(awardMonth),
        note: note.trim() || null,
      });
      toast.success(
        `Pontos atribuídos: ${data.created} novo(s), ${data.updated} atualizado(s) em "${data.event_name}".`,
      );
      setSelected({});
      setNote('');
      onAwarded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível atribuir os pontos.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-gray-800 bg-gray-900">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <CalendarPlus size={18} className="text-amber-300" />
            Dados do lançamento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm text-gray-300">Evento</label>
              <div className="mt-1 flex items-center gap-2">
                <Select value={eventId} onValueChange={setEventId}>
                  <SelectTrigger className="bg-gray-950 border-gray-800 text-white">
                    <SelectValue placeholder={loading ? 'Carregando...' : 'Selecione um evento'} />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-950 border-gray-800">
                    {events.map((e) => (
                      <SelectItem key={e.id} value={e.id} className="text-white">
                        {e.name}
                      </SelectItem>
                    ))}
                    {events.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">Nenhum evento ativo</div>
                    )}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  className="border-gray-700 text-gray-200 shrink-0"
                  onClick={() => setShowCreate(true)}
                  aria-label="Novo evento"
                >
                  <Plus size={16} />
                </Button>
              </div>
              {selectedEvent && (
                <p className="mt-1 text-xs text-gray-500">
                  Pontuação padrão: {selectedEvent.default_points} pts
                  {selectedEvent.description ? ` · ${selectedEvent.description}` : ''}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-300">Mês da participação</label>
              <Input
                type="month"
                value={awardMonth}
                onChange={(e) => e.target.value && setAwardMonth(e.target.value)}
                className="mt-1 w-48 bg-gray-950 border-gray-800 text-white"
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-300">Nota (opcional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex.: presença confirmada na lista"
              className="mt-1 bg-gray-950 border-gray-800 text-white"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-gray-800 bg-gray-900">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Users size={18} className="text-indigo-300" />
            Membros ({selectedIds.length} selecionado{selectedIds.length === 1 ? '' : 's'})
          </CardTitle>
          <div className="relative w-full sm:w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome..."
              className="pl-9 bg-gray-950 border-gray-800 text-white"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={`m-skel-${i}`} className="h-10 w-full bg-gray-800" />
              ))}
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-800 divide-y divide-gray-800/70">
              {filteredMembers.map((m) => {
                const row = selected[m.id];
                const checked = Boolean(row);
                return (
                  <div key={m.id} className="flex items-center gap-3 px-3 py-2">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(value) => toggleMember(m, value === true)}
                      aria-label={`Selecionar ${m.full_name}`}
                    />
                    <span className="flex-1 text-sm text-gray-200">{m.full_name}</span>
                    {checked && (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min={0}
                          value={row.points}
                          onChange={(e) => setMemberPoints(m.id, e.target.value)}
                          className="w-24 h-8 bg-gray-950 border-gray-800 text-white text-right"
                          aria-label={`Pontos de ${m.full_name}`}
                        />
                        <span className="text-xs text-gray-500">pts</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredMembers.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-gray-500">Nenhum membro encontrado.</div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={handleSubmit} disabled={submitting || selectedIds.length === 0}>
              {submitting ? 'Atribuindo...' : `Atribuir a ${selectedIds.length} membro${selectedIds.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        </CardContent>
      </Card>

      <EventFormDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        event={null}
        onSaved={reloadEvents}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Aba 2 — Eventos & histórico
// ---------------------------------------------------------------------------
const HistoryTab = ({ events, loading, reloadEvents, refreshKey }) => {
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState('');
  const [awards, setAwards] = useState([]);
  const [loadingAwards, setLoadingAwards] = useState(false);

  const loadAwards = async (eventId) => {
    setLoadingAwards(true);
    try {
      const { data } = await eventsAPI.listEventAwards(eventId);
      setAwards(data.items || []);
    } catch {
      toast.error('Não foi possível carregar os lançamentos do evento.');
    } finally {
      setLoadingAwards(false);
    }
  };

  useEffect(() => {
    if (expandedId) loadAwards(expandedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedId, refreshKey]);

  const toggleExpand = (eventId) => {
    setExpandedId((prev) => (prev === eventId ? '' : eventId));
  };

  const handleToggleActive = async (event) => {
    try {
      await eventsAPI.updateEvent(event.id, { is_active: !event.is_active });
      toast.success(event.is_active ? 'Evento desativado.' : 'Evento reativado.');
      reloadEvents();
    } catch {
      toast.error('Não foi possível atualizar o evento.');
    }
  };

  const handleDelete = async (event) => {
    if (!window.confirm(`Apagar o evento "${event.name}"?`)) return;
    try {
      const { data } = await eventsAPI.deleteEvent(event.id);
      toast.success(data.message);
      reloadEvents();
    } catch {
      toast.error('Não foi possível apagar o evento.');
    }
  };

  const handleChangeMonth = async (award, month) => {
    if (!month || month === award.award_date.slice(0, 7)) return;
    try {
      await eventsAPI.updateAwardDate(award.id, monthToAwardDate(month));
      toast.success('Mês da atribuição atualizado.');
      loadAwards(expandedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível alterar o mês.');
    }
  };

  const handleRevoke = async (award) => {
    const reason = window.prompt(`Revogar ${award.points} pts de ${award.full_name}? Motivo (opcional):`);
    if (reason === null) return;
    try {
      await eventsAPI.revokeAward(award.id, reason || null);
      toast.success('Pontos revogados.');
      loadAwards(expandedId);
      reloadEvents();
    } catch {
      toast.error('Não foi possível revogar.');
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-gray-800 bg-gray-900">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <History size={18} className="text-amber-300" />
            Eventos
          </CardTitle>
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            <Plus size={14} className="mr-1" />
            Novo evento
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={`ev-skel-${i}`} className="h-12 w-full bg-gray-800" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="rounded-lg border border-gray-800 bg-gray-950 px-4 py-6 text-center text-gray-300">
              Nenhum evento cadastrado ainda.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-400 text-left">
                    <th className="px-3 py-3 font-medium">Evento</th>
                    <th className="px-3 py-3 font-medium">Pontuação padrão</th>
                    <th className="px-3 py-3 font-medium">Lançamentos ativos</th>
                    <th className="px-3 py-3 font-medium">Status</th>
                    <th className="px-3 py-3 font-medium text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <React.Fragment key={event.id}>
                      <tr className="border-b border-gray-800/80 text-gray-200">
                        <td className="px-3 py-3">
                          <button
                            type="button"
                            className="font-medium text-white hover:underline"
                            onClick={() => toggleExpand(event.id)}
                          >
                            {event.name}
                          </button>
                          {event.description && (
                            <p className="text-xs text-gray-500">{event.description}</p>
                          )}
                        </td>
                        <td className="px-3 py-3 text-gray-300">{event.default_points} pts</td>
                        <td className="px-3 py-3 text-gray-300">{event.awards_count}</td>
                        <td className="px-3 py-3">
                          {event.is_active ? (
                            <Badge className="border border-emerald-400/30 bg-emerald-950/70 text-emerald-200">Ativo</Badge>
                          ) : (
                            <Badge className="border border-gray-600 bg-gray-800 text-gray-300">Inativo</Badge>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-gray-400 hover:text-white"
                              onClick={() => {
                                setEditing(event);
                                setShowForm(true);
                              }}
                              aria-label={`Editar ${event.name}`}
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-gray-700 text-gray-200 h-8"
                              onClick={() => handleToggleActive(event)}
                            >
                              {event.is_active ? 'Desativar' : 'Reativar'}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-400 hover:text-red-300"
                              onClick={() => handleDelete(event)}
                              aria-label={`Apagar ${event.name}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {expandedId === event.id && (
                        <tr className="bg-gray-950/60">
                          <td colSpan={5} className="px-3 py-3">
                            {loadingAwards ? (
                              <Skeleton className="h-16 w-full bg-gray-800" />
                            ) : awards.length === 0 ? (
                              <p className="text-sm text-gray-500">Nenhum ponto atribuído neste evento.</p>
                            ) : (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500 text-left">
                                    <th className="px-2 py-1 font-medium">Membro</th>
                                    <th className="px-2 py-1 font-medium">Pontos</th>
                                    <th className="px-2 py-1 font-medium">Data</th>
                                    <th className="px-2 py-1 font-medium">Nota</th>
                                    <th className="px-2 py-1 font-medium">Status</th>
                                    <th className="px-2 py-1 font-medium text-right">Ação</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {awards.map((award) => (
                                    <tr key={award.id} className="text-gray-300 border-t border-gray-800/60">
                                      <td className="px-2 py-1.5">{award.full_name}</td>
                                      <td className="px-2 py-1.5">{award.points}</td>
                                      <td className="px-2 py-1.5">
                                        {award.status === 'active' ? (
                                          <Input
                                            type="month"
                                            value={award.award_date.slice(0, 7)}
                                            onChange={(e) => handleChangeMonth(award, e.target.value)}
                                            className="h-7 w-36 bg-gray-950 border-gray-800 text-white text-xs"
                                            aria-label="Mês da atribuição"
                                          />
                                        ) : (
                                          award.award_date
                                        )}
                                      </td>
                                      <td className="px-2 py-1.5 text-gray-500">{award.note || '—'}</td>
                                      <td className="px-2 py-1.5">
                                        {award.status === 'active' ? (
                                          <span className="text-emerald-300">ativo</span>
                                        ) : (
                                          <span className="text-gray-500">revogado</span>
                                        )}
                                      </td>
                                      <td className="px-2 py-1.5 text-right">
                                        {award.status === 'active' && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 text-red-400 hover:text-red-300"
                                            onClick={() => handleRevoke(award)}
                                          >
                                            <Undo2 size={12} className="mr-1" />
                                            Revogar
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <EventFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        event={editing}
        onSaved={reloadEvents}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------
const AdminEventPointsPage = () => {
  const [activeEvents, setActiveEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadEvents = async () => {
    try {
      const [active, all] = await Promise.all([
        eventsAPI.listEvents({ activeOnly: true }),
        eventsAPI.listEvents({ activeOnly: false }),
      ]);
      setActiveEvents(active.data || []);
      setAllEvents(all.data || []);
    } catch {
      toast.error('Não foi possível carregar os eventos.');
    }
  };

  const loadMembers = async () => {
    try {
      const { data } = await adminAPI.getUsers();
      setMembers(
        (data || [])
          .filter((u) => u.role === 'DEFAULT' && u.is_active)
          .sort((a, b) => a.full_name.localeCompare(b.full_name)),
      );
    } catch {
      toast.error('Não foi possível carregar os membros.');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadEvents(), loadMembers()]);
      setLoading(false);
    })();
  }, []);

  const handleAwarded = () => {
    loadEvents();
    setRefreshKey((k) => k + 1);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-event-points-page">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Pontos de Evento</h1>
          <p className="text-gray-400 mt-1">
            Atribua pontos de participação em eventos da Elite. Os pontos entram no ranking do período.
          </p>
        </div>

        <Tabs defaultValue="assign" className="w-full">
          <TabsList className="bg-gray-900/60">
            <TabsTrigger value="assign" className="data-[state=active]:bg-white data-[state=active]:text-primary-foreground">
              <CalendarPlus size={16} className="mr-2" />
              Atribuir pontos
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:text-primary-foreground">
              <History size={16} className="mr-2" />
              Eventos &amp; histórico
            </TabsTrigger>
          </TabsList>

          <TabsContent value="assign" className="mt-6">
            <AssignTab
              events={activeEvents}
              members={members}
              loading={loading}
              reloadEvents={loadEvents}
              onAwarded={handleAwarded}
            />
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <HistoryTab
              events={allEvents}
              loading={loading}
              reloadEvents={loadEvents}
              refreshKey={refreshKey}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AdminEventPointsPage;
